import { useState, useRef, useEffect, useMemo } from 'react';

import { ChatBubble } from './ChatBubble';
import { Button } from '../base/Button';
import { TextField } from '../base/TextField';
import useOrbitChatMutation from '../../data/orbit/useOrbitChatMutation';
import useOrbitChatMessages from '../../data/orbit/useOrbitChatMessages';
import { ChatMessage, ChatRole } from '../../types/Orbit/Chat';
import { useUserContext } from '../../contexts/UserContext';
import { TextArea } from '../base/TextArea';
import { CodeBracketIcon } from '@heroicons/react/24/outline';
import { Popover, Transition } from '@headlessui/react';
import { isProd } from '../../config';
import { Avatar } from '../shared/Avatar';
import useOrganisationUsers from '../../data/organisationUsers/useOrganisationUsers';

interface ChatProps {
  className?: string;
  chatId: string;
  chatTitle?: string;
  isExistingChat?: boolean;
}

export function Chat({
  className,
  chatId,
  chatTitle,
  isExistingChat,
}: ChatProps) {
  const { userId } = useUserContext();
  const { users } = useOrganisationUsers();
  const [title, setTitle] = useState<string | undefined>(undefined);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const focusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const participants = useMemo(() => {
    const seen = new Set<string>();
    const list: string[] = [];
    for (const m of messages) {
      if (m.userId) {
        const id = String(m.userId);
        if (!seen.has(id)) {
          seen.add(id);
          list.push(id);
        }
      }
    }
    return list;
  }, [messages]);

  const { sendMessage } = useOrbitChatMutation();
  const { messages: loadedMessages } = useOrbitChatMessages(
    isExistingChat ? chatId : undefined
  );

  useEffect(() => {
    const loadedChat = loadedMessages.data?.[0];
    const loadedChatMessages = Array.isArray(loadedChat?.messages)
      ? loadedChat.messages
      : [];

    if (!loadedChat) {
      setMessages([]);
      return;
    }

    setMessages(
      loadedChatMessages.map((m) => ({
        ...m,
        timestamp: new Date(m.timestamp),
      }))
    );
  }, [loadedMessages.data]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sendMessage.isLoading]);

  useEffect(() => {
    return () => {
      if (focusTimeoutRef.current !== null) {
        clearTimeout(focusTimeoutRef.current);
      }
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || sendMessage.isLoading) return;

    const userMsg: ChatMessage = {
      role: ChatRole.User,
      userId: userId,
      content: text,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInput('');

    sendMessage.mutate(
      { chatId, message: text },
      {
        onSuccess: (data) => {
          const response = data?.[0];

          if (!response?.reply) {
            const emptyResponseMsg: ChatMessage = {
              role: ChatRole.Assistant,
              content: 'Sorry, no reply was returned. Please try again.',
              timestamp: new Date(),
            };

            setMessages((prev) => [...prev, emptyResponseMsg]);
            return;
          }

          const assistantMsg: ChatMessage = {
            role: ChatRole.Assistant,
            content: response.reply,
            timestamp: new Date(),
          };

          if (response.title) {
            setTitle(response.title);
          }

          setMessages((prev) => [...prev, assistantMsg]);
        },
        onError: () => {
          const errorMsg: ChatMessage = {
            role: ChatRole.Assistant,
            content: 'Sorry, something went wrong. Please try again.',
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, errorMsg]);
        },
        onSettled: () => {
          focusTimeoutRef.current = setTimeout(() => {
            inputRef.current?.focus();
            focusTimeoutRef.current = null;
          }, 150);
        },
      }
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (input.trim() && !sendMessage.isLoading) {
        handleSubmit(e as unknown as React.FormEvent);
      }
      return;
    }

    if (e.key !== 'ArrowUp') return;
    if (input.trim().length > 0) return;
    e.preventDefault();

    const lastUser = [...messages]
      .reverse()
      .find((m) => m.role === ChatRole.User);
    const source =
      lastUser ?? (messages.length ? messages[messages.length - 1] : null);
    if (!source) return;

    setInput(source.content);
    focusTimeoutRef.current = setTimeout(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        const len = source.content.length;
        try {
          el.setSelectionRange(len, len);
        } catch { }
      }
      focusTimeoutRef.current = null;
    }, 50);
  };

  return (
    <div className={`flex flex-col h-full ${className ?? ''}`}>
      <div className="px-4 py-3 flex items-center justify-between border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <h2 className="text-lg font-medium dark:text-white">
          {title ?? chatTitle ?? 'New Chat'}
        </h2>
        <div className="flex items-center gap-2">
          <div className="flex items-center -space-x-3">
            {!isExistingChat ? (
              <div className="inline-block ring-2 ring-white dark:ring-gray-800 rounded-full">
                <Avatar id={userId!} forename="Liam" surname="Barry" size={8} />
              </div>
            ) : (
              participants.map((id) => {
                const user = users.data?.find((u) => u.id === id);
                return (
                  <div key={id} className="inline-block ring-2 ring-white dark:ring-gray-800 rounded-full">
                    <Avatar id={id} forename={user?.forename ?? ''} surname={user?.surname ?? ''} size={8} />
                  </div>
                )
              })
            )}
          </div>
          {!isProd && (
            <Popover className="relative">
              <Popover.Button className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800">
                <CodeBracketIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </Popover.Button>

              <Transition
                enter="transition duration-100 ease-out"
                enterFrom="transform scale-95 opacity-0"
                enterTo="transform scale-100 opacity-100"
                leave="transition duration-75 ease-out"
                leaveFrom="transform scale-100 opacity-100"
                leaveTo="transform scale-95 opacity-0"
              >
                <Popover.Panel className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg p-3 z-10">
                  <div className="text-sm text-gray-700 dark:text-gray-200 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">Debug</span>
                      <span className="text-xs text-gray-400">{isExistingChat ? 'Existing' : 'New'}</span>
                    </div>
                    <div className="text-xs text-gray-400">Chat ID: <span className="font-mono text-gray-700 dark:text-gray-200 break-all">{chatId}</span></div>
                    <div className="text-xs text-gray-400">Title: <span className="text-gray-700 dark:text-gray-200">{title ?? chatTitle ?? '—'}</span></div>
                    <div className="text-xs text-gray-400">User ID: <span className="text-gray-700 dark:text-gray-200">{userId ?? '—'}</span></div>
                    <div className="text-xs text-gray-400">Messages: <span className="text-gray-700 dark:text-gray-200">{messages.length}</span></div>
                    <div className="text-xs text-gray-400 truncate">Last: <span className="text-gray-700 dark:text-gray-200">{messages.length ? messages[messages.length - 1].content : '—'}</span></div>

                    <div className="flex gap-2 pt-2">
                      <button
                        type="button"
                        onClick={() => navigator.clipboard?.writeText(JSON.stringify(messages, null, 2))}
                        className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 rounded"
                      >
                        Copy JSON
                      </button>
                      <button
                        type="button"
                        onClick={() => console.log('Debug messages:', messages)}
                        className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 rounded"
                      >
                        Log
                      </button>
                    </div>
                  </div>
                </Popover.Panel>
              </Transition>
            </Popover>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto flex flex-col gap-3 p-4 bg-gray-400/5 dark:bg-white/5">
        {messages.length === 0 && (
          <p className="text-center text-gray-400 mt-10 text-sm">
            Send a message to start chatting.
          </p>
        )}
        {messages.map((msg, index) => (
          <ChatBubble key={index} message={msg} />
        ))}
        {sendMessage.isLoading && (
          <div className="self-start flex items-center gap-1.5 px-4 py-3 rounded-xl rounded-bl-sm bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600">
            <span
              className="w-2 h-2 rounded-full bg-gray-400 animate-pulse-fast"
              style={{ animationDelay: '0ms' }}
            />
            <span
              className="w-2 h-2 rounded-full bg-gray-400 animate-pulse-fast"
              style={{ animationDelay: '200ms' }}
            />
            <span
              className="w-2 h-2 rounded-full bg-gray-400 animate-pulse-fast"
              style={{ animationDelay: '400ms' }}
            />
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <form
        onSubmit={handleSubmit}
        className="flex gap-2 p-3 border-t border-gray-200 dark:border-gray-700 shadow-sm"
      >
        <TextArea
          rows={2}
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.currentTarget.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message..."
          disabled={sendMessage.isLoading}
          autoFocus
        />

        {/* <TextField
          ref={inputRef}
          fullWidth
          value={input}
          onChange={(e) => setInput(e.currentTarget.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message..."
          disabled={sendMessage.isLoading}
          autoFocus
        /> */}
        <Button type="submit" disabled={sendMessage.isLoading || !input.trim()}>
          Send
        </Button>
      </form>
    </div>
  );
}
